import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { capitalize } from "common/utils/strings";
import { DateTime } from "luxon";
import { DimensionedNumberInput } from "./DimensionedNumberInput";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { useTranslation } from "react-i18next";
import ChecklistIcon from "@mui/icons-material/Checklist";
import React, { FC, useState } from "react";

interface ExemptionButtonProps {
  serial: string;
  fieldId?: string;
}
const EIGHT_HOURS_TO_SECONDS = 28_800;
export const ExemptionButton: FC<ExemptionButtonProps> = ({
  serial,
  fieldId,
}) => {
  const { t, i18n } = useTranslation();
  const { data, error } = rtcJobsApi.endpoints.getChecklistExemption.useQuery(
    {
      serial,
      fieldId,
    },
    { refetchOnMountOrArgChange: 10, pollingInterval: 10_000 }
  );
  let exemption = data;
  if (error) {
    exemption = undefined;
  }
  const [setChecklistExemption, { isLoading }] =
    rtcJobsApi.endpoints.setChecklistExemption.useMutation();
  const [duration, setDuration] = useState<number>(EIGHT_HOURS_TO_SECONDS);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const createExemption = async (): Promise<void> => {
    await setChecklistExemption({
      serial,
      validDuration: { seconds: duration, nanos: 0 },
      fieldId,
    });
    setDialogOpen(false);
  };

  const modal = (
    <Dialog open={dialogOpen}>
      <DialogTitle>
        {capitalize(t("views.autotractor.checklists.createExemption"))}
      </DialogTitle>
      <DialogContent className="flex flex-col gap-2">
        <DialogContentText>
          {t("views.autotractor.checklists.exemptionDialog")}
        </DialogContentText>
        <DimensionedNumberInput
          textFieldProps={{ className: "mt-2" }}
          label={capitalize(t("views.autotractor.checklists.duration"))}
          storedValue={duration}
          storedUnit="s"
          displayUnits={{
            metric: "h",
            imperial: "h",
          }}
          onChange={(n) => n && setDuration(n)}
          localSystem={"metric"} // doesn't matter, we display hours everywhere
        />
      </DialogContent>
      {isLoading && <LinearProgress variant="indeterminate" />}
      <DialogActions>
        <Button
          variant="contained"
          color="info"
          disabled={isLoading}
          onClick={() => setDialogOpen(false)}
        >
          {t("utils.actions.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={createExemption}
          disabled={isLoading}
        >
          {t("utils.actions.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div className="flex flex-col">
      {exemption && exemption.expiresAt && exemption.valid && (
        <Tooltip title={t("views.autotractor.checklists.validUntil")}>
          <Button
            className="flex my-auto gap-1"
            onClick={() => setDialogOpen(true)}
            color="warning"
            size="small"
          >
            <ChecklistIcon color="warning" />
            <Typography className="flex my-auto mx-auto">
              {DateTime.fromISO(exemption.expiresAt).toLocaleString(
                { timeStyle: "short", dateStyle: "short" },
                { locale: i18n.language }
              )}
            </Typography>
          </Button>
        </Tooltip>
      )}
      {!exemption && (
        <Button size="small" onClick={() => setDialogOpen(true)} color="info">
          {t("views.autotractor.checklists.createExemption")}
        </Button>
      )}
      {modal}
    </div>
  );
};
